import { isSSR } from '@k2/core/util'
import type { Platform } from '@k2/domain/campaigns'
import { defineStore } from 'pinia'
import { computed, type Ref, ref } from 'vue'
import { useRouter } from 'vue-router'
import { type ApiFailure, useApiLoad, useApiRequest } from '@/api'
import { type CampaignDeliverable, type DeliverableChanges, useDeliverablesResource } from '@/deliverables/resources'
import type { CampaignStart } from '../model'
import { type Campaign, type CampaignListItem, useCampaignsResource } from '../resources'

export const useCampaignStore = defineStore('campaign', () => {
  const { getCampaign, getCampaigns, setCampaign } = useCampaignsResource()
  const {
    getCampaignDeliverables,
    setCampaignDeliverable,
    setDeliverableSchedule,
    updateDeliverable,
    setDeliverablePlatforms,
    setDeliverableDuplicate,
    delDeliverable
  } = useDeliverablesResource()
  const router = useRouter()
  const { runScoped } = useApiRequest()
  const { run, reset, loading, failure } = useApiLoad()

  const lists = ref<Record<string, CampaignListItem[]>>({})
  const loadedBrandIds = ref<string[]>([])
  const campaigns = ref<Record<string, Campaign>>({})
  const deliverables = ref<Record<string, CampaignDeliverable[]>>({})
  const creating = ref(false)
  const createFailure = ref<ApiFailure | undefined>()
  const scheduling = ref<string | null>(null)
  const adding = ref(false)
  const addFailure = ref<ApiFailure | undefined>()
  const postFailure = ref<ApiFailure | undefined>()
  const campaignFailures = ref<Record<string, ApiFailure>>({})
  const deliverableFailures = ref<Record<string, ApiFailure>>({})
  const loadFailures = computed(() => ({ ...deliverableFailures.value, ...campaignFailures.value }))
  // A picker write made while one is in flight is kept, latest wins, and sent when the first lands.
  const queuedSchedules = new Map<string, string | null>()

  let listedOrganizationId: string | undefined

  const loadCampaignsForBrands = async (organizationId: string, brandIds: string[]) => {
    if (isSSR) return

    if (listedOrganizationId !== organizationId) {
      listedOrganizationId = organizationId
      lists.value = {}
      loadedBrandIds.value = []
      reset()
    }

    if (!brandIds.length) return

    const rows = await run<Record<string, CampaignListItem[]>>(async () => {
      const responses = await Promise.all(
        brandIds.map(async brandId => ({ brandId, response: await getCampaigns(organizationId, brandId) }))
      )
      const failed = responses.find(({ response }) => response.error)
      if (failed) return failed.response

      return {
        data: Object.fromEntries(responses.map(({ brandId, response }) => [brandId, response.data ?? []])),
        error: null
      }
    })
    if (!rows) return

    lists.value = { ...lists.value, ...rows }
    loadedBrandIds.value = [...new Set([...loadedBrandIds.value, ...brandIds])]
  }

  const create = async (organizationId: string, brandId: string, start: CampaignStart) => {
    creating.value = true
    createFailure.value = undefined
    try {
      const created = runScoped<{ id: string }>(() => setCampaign(organizationId, brandId))
      const campaign = await created.result
      if (!campaign) {
        createFailure.value = created.failure.value
        return false
      }
      await router.replace({
        name: start === 'brief' ? 'campaign-discovery' : 'campaign',
        params: { organizationId, campaignId: campaign.id }
      })
      return true
    } finally {
      creating.value = false
    }
  }

  const recordFailure = (failures: Ref<Record<string, ApiFailure>>, campaignId: string, failed: ApiFailure | undefined) => {
    const { [campaignId]: _, ...rest } = failures.value
    failures.value = failed ? { ...rest, [campaignId]: failed } : rest
  }

  const loadCampaign = async (organizationId: string, campaignId: string) => {
    if (isSSR) return
    recordFailure(campaignFailures, campaignId, undefined)
    const request = runScoped<Campaign>(() => getCampaign(organizationId, campaignId))
    const row = await request.result
    recordFailure(campaignFailures, campaignId, request.failure.value)
    if (row) campaigns.value = { ...campaigns.value, [campaignId]: row }
    return row
  }

  const schedule = async (
    organizationId: string,
    campaignId: string,
    deliverableId: string,
    scheduledAt: string | null
  ): Promise<boolean> => {
    if (isSSR) return false

    if (scheduling.value === deliverableId) {
      queuedSchedules.set(deliverableId, scheduledAt)
      return false
    }

    scheduling.value = deliverableId
    const request = runScoped<{ scheduledAt: string | null }>(() => setDeliverableSchedule(organizationId, deliverableId, scheduledAt))
    const moved = await request.result

    scheduling.value = null
    postFailure.value = request.failure.value

    if (moved) patchPost(campaignId, deliverableId, { scheduledAt: moved.scheduledAt })

    const queued = queuedSchedules.get(deliverableId)
    if (queued === undefined) return !!moved
    queuedSchedules.delete(deliverableId)
    return schedule(organizationId, campaignId, deliverableId, queued)
  }

  const loadDeliverables = async (organizationId: string, campaignId: string) => {
    if (isSSR) return
    recordFailure(deliverableFailures, campaignId, undefined)
    const request = runScoped<CampaignDeliverable[]>(() => getCampaignDeliverables(organizationId, campaignId))
    const rows = await request.result
    recordFailure(deliverableFailures, campaignId, request.failure.value)
    if (rows) deliverables.value = { ...deliverables.value, [campaignId]: rows }
    return rows
  }

  // D2: a new post is created as it is, and opened at once; its shape is the brand's usual one (D5).
  const addDeliverable = async (organizationId: string, campaignId: string) => {
    if (isSSR || adding.value) return null
    adding.value = true
    addFailure.value = undefined
    try {
      const request = runScoped<{ deliverable: { id: string } }>(() => setCampaignDeliverable(organizationId, campaignId))
      const created = await request.result
      addFailure.value = request.failure.value
      return created?.deliverable.id ?? null
    } finally {
      adding.value = false
    }
  }

  const setPosts = (campaignId: string, next: (rows: CampaignDeliverable[]) => CampaignDeliverable[]) => {
    deliverables.value = { ...deliverables.value, [campaignId]: next(deliverables.value[campaignId] ?? []) }
  }

  const patchPost = (campaignId: string, deliverableId: string, changes: Partial<CampaignDeliverable>) =>
    setPosts(campaignId, rows => rows.map(row => (row.id === deliverableId ? { ...row, ...changes } : row)))

  const writePost = async <T>(call: () => Promise<{ data: unknown; error: unknown }>) => {
    const request = runScoped<T>(call)
    const result = await request.result
    postFailure.value = request.failure.value
    return { ok: !request.failure.value, result }
  }

  const updatePost = async (organizationId: string, campaignId: string, deliverableId: string, changes: DeliverableChanges) => {
    if (!(await writePost(() => updateDeliverable(organizationId, deliverableId, changes))).ok) return false
    patchPost(campaignId, deliverableId, { ...changes, updatedAt: new Date().toISOString() })
    return true
  }

  const choosePlatforms = async (organizationId: string, campaignId: string, deliverableId: string, platforms: Platform[]) => {
    if (!(await writePost(() => setDeliverablePlatforms(organizationId, deliverableId, platforms))).ok) return false
    patchPost(campaignId, deliverableId, { platforms })
    return true
  }

  const duplicatePost = async (organizationId: string, campaignId: string, deliverableId: string) => {
    const { result: copy } = await writePost<{ id: string }>(() => setDeliverableDuplicate(organizationId, deliverableId))
    if (!copy) return null
    setPosts(campaignId, rows =>
      rows.flatMap(row =>
        row.id === deliverableId
          ? [
              row,
              {
                ...row,
                id: copy.id,
                scheduledAt: null,
                status: row.status === 'published' ? ('ready' as const) : row.status,
                updatedAt: new Date().toISOString()
              }
            ]
          : [row]
      )
    )
    return copy.id
  }

  const removePost = async (organizationId: string, campaignId: string, deliverableId: string) => {
    if (!(await writePost(() => delDeliverable(organizationId, deliverableId))).ok) return false
    setPosts(campaignId, rows => rows.filter(row => row.id !== deliverableId))
    return true
  }

  return {
    lists,
    loadedBrandIds,
    campaigns,
    deliverables,
    loading,
    failure,
    creating,
    createFailure,
    adding,
    addFailure,
    postFailure,
    loadFailures,
    loadCampaignsForBrands,
    create,
    loadCampaign,
    loadDeliverables,
    addDeliverable,
    schedule,
    updatePost,
    choosePlatforms,
    duplicatePost,
    removePost
  }
})
